import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    Chip,
    Divider,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import ApiReferenceTab, { MetadataChip } from './ApiReferenceTab';
import { FieldTable } from './_shared';

// ─── Analytics Schema Reference ───────────────────────────────────────────────

const QUERY_PARAMS = [
    { name: 'from', type: 'string (date)', required: false, default: '—', description: 'Start date filter on shipment_date. Format: YYYY-MM-DD.' },
    { name: 'to', type: 'string (date)', required: false, default: '—', description: 'End date filter on shipment_date (inclusive). Format: YYYY-MM-DD.' },
    { name: 'page', type: 'integer', required: false, default: '1', description: 'Page number (1-based).' },
    { name: 'batchSize', type: 'integer', required: false, default: '1000', description: 'Records per page. Maximum 10,000.' },
    { name: 'apiKey', type: 'string', required: false, default: '—', description: 'API key as an alternative to the Authorization: Bearer header.' },
];

const RESPONSE_META = [
    { name: 'code', type: 'integer', description: 'HTTP status code (200).' },
    { name: 'page', type: 'integer', description: 'Current page number.' },
    { name: 'totalPages', type: 'integer', description: 'Total number of pages.' },
    { name: 'totalCount', type: 'integer', description: 'Total matching shipment records.' },
    { name: 'batchSize', type: 'integer', description: 'Records returned in this page.' },
    { name: 'data', type: 'ShipmentRecord[]', description: 'Array of shipment records.' },
];

const SHIPMENT_FIELDS = [
    { name: 'customer_code', type: 'integer | null', description: 'Internal company ID.' },
    { name: 'customer_reference', type: 'string | null', description: 'Customer-facing reference number.' },
    { name: 'shipment_reference', type: 'string | null', description: 'Internal shipment reference.' },
    { name: 'origin_lat / origin_long', type: 'number | null', description: 'Latitude / longitude of origin.' },
    { name: 'origin_city', type: 'string | null', description: 'City name of the shipment origin.' },
    { name: 'destination_lat / destination_long', type: 'number | null', description: 'Latitude / longitude of destination.' },
    { name: 'destination_city', type: 'string | null', description: 'City name of the destination.' },
    { name: 'cooling', type: 'boolean | null', description: 'Whether the shipment required refrigerated transport.' },
    { name: 'total_weight', type: 'number | null', description: 'Gross weight in kg.' },
    { name: 'total_distance_km', type: 'number | null', description: 'Total journey distance across all legs (km).' },
    { name: 'total_co2', type: 'number | null', description: 'Total Tank-to-Wheel (TTW) CO₂e emissions in kg.' },
    { name: 'total_co2e', type: 'number | null', description: 'Total Well-to-Tank (WTT) upstream CO₂e emissions in kg.' },
    { name: 'total_co2wtw', type: 'number | null', description: 'Total Well-to-Wheel (WTW) CO₂e emissions in kg (TTW + WTT).' },
    { name: '{mode}_distance_km', type: 'number | null', description: 'Distance for mode leg (km). null if mode not used.' },
    { name: '{mode}_co2', type: 'number | null', description: 'TTW emissions for mode leg (kg).' },
    { name: '{mode}_co2e', type: 'number | null', description: 'WTT emissions for mode leg (kg).' },
    { name: '{mode}_co2wtw', type: 'number | null', description: 'WTW emissions for mode leg (kg).' },
    { name: 'date_of_calculation', type: 'string', description: 'Calculation timestamp (GMT). Format: YYYY-MM-DD HH:MM:SS.' },
    { name: 'pdf_url', type: 'string (uri)', description: 'URL to the PDF emission report for this shipment.' },
];

const MODES = ['road', 'sea', 'air', 'rail', 'iww', 'ferry', 'hub'];

function AnalyticsSchemaContent() {
    return (
        <Box>
            {/* Endpoint header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <StorageIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h5" fontWeight={700}>Analytics API Reference</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Programmatic access to your company's shipment emissions data for integration into your own analytics systems and dashboards.
            </Typography>

            {/* Endpoint box */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'rgba(25, 118, 210, 0.04)', borderColor: '#1976d2' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label="GET" size="small" sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 700, fontSize: '0.75rem' }} />
                    <Typography variant="body1" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>
                        /api/analytics/shipments
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Returns a paginated list of shipment records with per-mode emission breakdowns, ordered by calculation date (newest first), scoped to the authenticated company's API key.
                </Typography>
            </Paper>

            {/* Auth */}
            <Alert icon={<InfoOutlinedIcon fontSize="small" />} severity="info" variant="outlined" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    Authenticate using your <strong>CarbonCare API key</strong> — same key used for the REST Calculation API — via{' '}
                    <code style={{ background: '#e3f2fd', padding: '1px 4px', borderRadius: 3 }}>Authorization: Bearer &lt;api-key&gt;</code>{' '}
                    header or{' '}
                    <code style={{ background: '#e3f2fd', padding: '1px 4px', borderRadius: 3 }}>?apiKey=&lt;api-key&gt;</code>{' '}
                    query parameter.
                </Typography>
            </Alert>

            {/* Query Parameters */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Query Parameters</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Default</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {QUERY_PARAMS.map(p => (
                            <TableRow key={p.name} hover>
                                <TableCell>
                                    <code style={{ fontWeight: 600, color: '#1565c0' }}>{p.name}</code>
                                </TableCell>
                                <TableCell>
                                    <code style={{ fontSize: '0.8rem', color: '#555' }}>{p.type}</code>
                                </TableCell>
                                <TableCell>
                                    {p.required
                                        ? <Chip label="required" size="small" color="warning" variant="outlined" />
                                        : <Typography variant="caption" color="text.secondary">optional</Typography>}
                                </TableCell>
                                <TableCell>
                                    <code style={{ fontSize: '0.8rem' }}>{p.default}</code>
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.85rem' }}>{p.description}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Response envelope */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5 }}>Response Envelope (200 OK)</Typography>
            <FieldTable rows={RESPONSE_META} />

            {/* ShipmentRecord fields */}
            <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>ShipmentRecord Fields</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Per-mode fields use prefix: <strong>{MODES.join(', ')}</strong>. Mode-specific fields are <code>null</code> when that transport mode was not used.
            </Typography>

            <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 0.5 }}>
                <Typography variant="body2">
                    Emission field suffixes: <code>_co2</code> = TTW (Tank-to-Wheel) · <code>_co2e</code> = WTT (Well-to-Tank, upstream) · <code>_co2wtw</code> = WTW total (TTW + WTT). All values in <strong>kg CO₂e</strong>.
                </Typography>
            </Alert>

            <FieldTable rows={SHIPMENT_FIELDS} nameFontSize="0.8rem" typeFontSize="0.78rem" />

            <Divider sx={{ mb: 2 }} />

            {/* Footer */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">
                    <strong>Note:</strong> This API is backed by the same data pipeline as the Analytics → Export Excel feature. Results are scoped to your company's API key and reflect the same shipments visible in the dashboard.
                </Typography>
            </Box>
        </Box>
    );
}

// ─── Metadata chips ────────────────────────────────────────────────────────────

const ANALYTICS_CHIPS: MetadataChip[] = [
    { label: 'OpenAPI 3.0.3', color: 'success', variant: 'outlined' },
    { label: 'REST / JSON', variant: 'outlined' },
    { label: 'GET /api/analytics/shipments', color: 'primary', variant: 'outlined' },
];

// ─── Tab Component ─────────────────────────────────────────────────────────────

// Drive the "Try it out" server off the same env var the dashboard uses for the
// backend, so it targets the correct API per environment (dev/staging/prod)
// instead of a host hardcoded in the spec. Analytics routes live under /analytics.
const ANALYTICS_SPEC = '/carboncare-analytics-openapi.yaml';
const ANALYTICS_API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/analytics`;
const ANALYTICS_SWAGGER_SRC =
    `/swagger-ui.html?spec=${ANALYTICS_SPEC}&apiBase=${encodeURIComponent(ANALYTICS_API_BASE)}`;

const AnalyticsApiTab: React.FC<{ apiKey?: string }> = ({ apiKey }) => (
    <ApiReferenceTab
        specFile={ANALYTICS_SPEC}
        swaggerIframeSrc={ANALYTICS_SWAGGER_SRC}
        metadataChips={ANALYTICS_CHIPS}
        schemaContent={<AnalyticsSchemaContent />}
        downloadFileName="carboncare-analytics-openapi.yaml"
        apiKey={apiKey}
    />
);

export default AnalyticsApiTab;
